import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChartBar, Funnel, Clock, CurrencyGbp, Trophy, ArrowDown } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";

const STAGE_COLORS = [
  "#6B7280", "#3B82F6", "#8B5CF6", "#A855F7", "#EC4899", "#F59E0B",
  "#F97316", "#EF4444", "#14B8A6", "#06B6D4", "#0EA5E9", "#10B981",
  "#22C55E", "#16A34A", "#C5A059", "#B8933F", "#1E3A8A", "#0F172A",
];

const PIE_COLORS = ["#3B82F6", "#14B8A6", "#10B981", "#6B7280", "#EF4444"];

export default function AnalyticsPage() {
  const [pipelineStats, setPipelineStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [interviewStats, setInterviewStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ps, rv, lb, is_] = await Promise.all([
          api.get("/dashboard/pipeline-stats"),
          api.get("/dashboard/revenue-forecast"),
          api.get("/dashboard/recruiter-leaderboard"),
          api.get("/dashboard/interview-stats"),
        ]);
        setPipelineStats(ps.data);
        setRevenue(rv.data);
        setLeaderboard(lb.data || []);
        setInterviewStats(is_.data);
      } catch { toast.error("Failed to load analytics"); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-40 skeleton-shimmer rounded-lg" />)}</div>;

  const funnelData = pipelineStats?.funnel?.filter(f => f.count > 0) || [];
  const interviewStatusData = interviewStats ? Object.entries(interviewStats.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.replace("_", " "), value: v })) : [];
  const conversionRates = pipelineStats?.conversion_rates?.filter(c => c.rate > 0 && c.rate < 100) || [];

  return (
    <div data-testid="analytics-page" className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Placement Analytics</h1>
        <p className="text-sm text-[#6B7280] mt-0.5">Pipeline health, revenue forecasts & performance</p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Pipeline", val: pipelineStats?.total_placements || 0, color: "#0F172A", icon: Funnel },
          { label: "Avg Time to Fill", val: `${pipelineStats?.avg_time_to_fill_days || 0}d`, color: "#F59E0B", icon: Clock },
          { label: "Weighted Forecast", val: `\u00a3${(revenue?.weighted_forecast || 0).toLocaleString()}`, color: "#1E3A8A", icon: CurrencyGbp },
          { label: "Confirmed Revenue", val: `\u00a3${(revenue?.confirmed_revenue || 0).toLocaleString()}`, color: "#16A34A", icon: CurrencyGbp },
          { label: "Pass Rate", val: `${interviewStats?.pass_rate || 0}%`, color: "#06B6D4", icon: ChartBar },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-2 mb-1">
              <k.icon size={14} className="text-[#6B7280]" />
              <p className="text-[10px] text-[#6B7280] font-medium uppercase">{k.label}</p>
            </div>
            <p className="text-xl font-bold" style={{ color: k.color }}>{k.val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Conversion Funnel */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><Funnel size={16} /> Pipeline Conversion Funnel</h2>
          {funnelData.length === 0 ? (
            <p className="text-sm text-[#6B7280] text-center py-8">No pipeline data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#6B7280" }} />
                <YAxis dataKey="label" type="category" width={90} tick={{ fontSize: 9, fill: "#6B7280" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 11 }}
                  formatter={(v, name, props) => [`${v} (${props.payload.conversion_rate}%)`, "Count"]} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {funnelData.map((_, i) => <Cell key={i} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Revenue by Stage */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><CurrencyGbp size={16} /> Revenue by Stage</h2>
          {(revenue?.pipeline_by_stage || []).length === 0 ? (
            <p className="text-sm text-[#6B7280] text-center py-8">No revenue data yet</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-[#F0F4FF] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[#6B7280] uppercase">Potential</p>
                  <p className="text-lg font-bold text-[#1E3A8A]">{"\u00a3"}{(revenue?.total_potential_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="bg-[#F0FFF4] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-[#6B7280] uppercase">Weighted</p>
                  <p className="text-lg font-bold text-[#16A34A]">{"\u00a3"}{(revenue?.weighted_forecast || 0).toLocaleString()}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                {revenue.pipeline_by_stage.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-[#F3F4F6]">
                    <span className="text-[#6B7280]">{s.stage}</span>
                    <div className="text-right">
                      <span className="font-medium text-[#0F172A]">{"\u00a3"}{s.value.toLocaleString()}</span>
                      <span className="text-[#6B7280] ml-2">(w: {"\u00a3"}{s.weighted.toLocaleString()})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Interview Stats Pie */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Interview Breakdown</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              {interviewStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={interviewStatusData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                      {interviewStatusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-[#6B7280] text-center py-12">No data</p>}
            </div>
            <div className="space-y-2">
              <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                <p className="text-[10px] text-[#6B7280] uppercase">Total</p>
                <p className="text-xl font-bold text-[#0F172A]">{interviewStats?.total || 0}</p>
              </div>
              <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                <p className="text-[10px] text-[#6B7280] uppercase">Avg Score</p>
                <p className="text-xl font-bold text-[#C5A059]">{interviewStats?.avg_score || "—"}/10</p>
              </div>
              <div className="bg-[#F9FAFB] rounded-lg p-2.5">
                <p className="text-[10px] text-[#6B7280] uppercase">With Scorecard</p>
                <p className="text-xl font-bold text-[#1E3A8A]">{interviewStats?.completed_with_scorecard || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stage Conversion Rates */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><ArrowDown size={16} /> Stage Conversion Rates</h2>
          {conversionRates.length === 0 ? (
            <p className="text-sm text-[#6B7280] text-center py-8">No conversion data</p>
          ) : (
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {conversionRates.map((c, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-[#F3F4F6]">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-[#6B7280] truncate">{c.from_stage} → {c.to_stage}</p>
                  </div>
                  <div className="w-24 h-2 bg-[#F3F4F6] rounded-full overflow-hidden flex-shrink-0">
                    <div className="h-full rounded-full transition-all" style={{ width: `${c.rate}%`, backgroundColor: c.rate > 70 ? "#16A34A" : c.rate > 40 ? "#F59E0B" : "#EF4444" }} />
                  </div>
                  <span className="text-xs font-medium text-[#0F172A] w-12 text-right">{c.rate}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recruiter Leaderboard */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><Trophy size={16} className="text-[#C5A059]" /> Recruiter Leaderboard</h2>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-[#6B7280] text-center py-8">No recruiter data</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-2 px-2 text-[#6B7280] font-medium">#</th>
                  <th className="text-left py-2 px-2 text-[#6B7280] font-medium">Recruiter</th>
                  <th className="text-center py-2 px-2 text-[#6B7280] font-medium">Vacancies</th>
                  <th className="text-center py-2 px-2 text-[#6B7280] font-medium">Pipeline</th>
                  <th className="text-center py-2 px-2 text-[#6B7280] font-medium">Placed</th>
                  <th className="text-center py-2 px-2 text-[#6B7280] font-medium">Interviews</th>
                  <th className="text-center py-2 px-2 text-[#6B7280] font-medium">Calls</th>
                  <th className="text-right py-2 px-2 text-[#6B7280] font-medium">Revenue</th>
                  <th className="text-right py-2 px-2 text-[#6B7280] font-medium">Score</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((r, i) => (
                  <tr key={r.user_id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`leaderboard-row-${i}`}>
                    <td className="py-2.5 px-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? "bg-[#C5A059] text-white" : i === 1 ? "bg-[#94A3B8] text-white" : i === 2 ? "bg-[#B45309] text-white" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="py-2.5 px-2">
                      <p className="font-medium text-[#0F172A]">{r.name}</p>
                      <p className="text-[10px] text-[#6B7280]">{r.role}</p>
                    </td>
                    <td className="text-center py-2.5 px-2 text-[#0F172A]">{r.vacancies}</td>
                    <td className="text-center py-2.5 px-2 text-[#8B5CF6] font-medium">{r.placements}</td>
                    <td className="text-center py-2.5 px-2 text-[#16A34A] font-bold">{r.placed}</td>
                    <td className="text-center py-2.5 px-2 text-[#06B6D4]">{r.interviews}</td>
                    <td className="text-center py-2.5 px-2 text-[#F59E0B]">{r.calls}</td>
                    <td className="text-right py-2.5 px-2 text-[#C5A059] font-medium">{"\u00a3"}{r.revenue.toLocaleString()}</td>
                    <td className="text-right py-2.5 px-2">
                      <span className="bg-[#0F172A] text-white px-2 py-0.5 rounded-full text-[10px] font-bold">{r.score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
